/* STELA keynote homepage: reveal on scroll, pinned scroll progress, cabin explorer,
   story chapters, lineup rail, card glow and counting numbers. No dependencies. */
(() => {
  const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* Reveal on scroll */
  const revealer = new IntersectionObserver((entries) => {
    entries.forEach((e) => {
      if (!e.isIntersecting) return;
      e.target.classList.add('is-in');
      revealer.unobserve(e.target);
    });
  }, { rootMargin: '0px 0px -10% 0px' });
  document.querySelectorAll('[data-kn-reveal]').forEach((el) => revealer.observe(el));

  /* Pinned sections get --p (0–1) while scrolling through them */
  const pins = [...document.querySelectorAll('[data-kn-pin]')];
  let ticking = false;
  const progress = () => {
    ticking = false;
    const vh = innerHeight;
    pins.forEach((s) => {
      const r = s.getBoundingClientRect();
      if (r.bottom < -vh || r.top > vh) return;
      const run = Math.max(1, r.height - vh);
      s.style.setProperty('--p', Math.min(1, Math.max(0, -r.top / run)).toFixed(4));
    });
  };
  if (pins.length && !reduce) {
    // Horizon scrolls .page-wrapper on desktop and the document on mobile; capture catches both.
    document.addEventListener('scroll', () => { if (!ticking) { ticking = true; requestAnimationFrame(progress); } }, { passive: true, capture: true });
    addEventListener('resize', progress);
    progress();
  } else {
    pins.forEach((s) => s.style.setProperty('--p', 1));
  }

  /* Cabin explorer */
  document.querySelectorAll('[data-kn-cabin]').forEach((root) => {
    const car = root.querySelector('.kn-cabin__car');
    const triggers = [...root.querySelectorAll('[data-zone]')];
    const panels = [...root.querySelectorAll('[data-zone-panel]')];
    const dots = [...root.querySelectorAll('.kn-cabin__dot')];

    /* Real 3D: load model-viewer and the model only when the section comes near */
    const stage = root.querySelector('[data-mv-src]');
    const mv = stage?.querySelector('model-viewer');
    const hots = [...root.querySelectorAll('.kn-cabin__hot')];
    const phone = matchMedia('(max-width: 989px)');
    const home = () => (phone.matches ? mv.dataset.homeMobile : mv.dataset.home);

    /* X-ray: fade the named glass materials out for interior zones */
    let glass = [];
    let alpha = 1;
    let fade = 0;
    const xray = (on) => {
      if (!glass.length) return;
      const to = on ? 0 : 1;
      const from = alpha;
      const t0 = performance.now();
      cancelAnimationFrame(fade);
      const step = (t) => {
        const k = reduce ? 1 : Math.min(1, (t - t0) / 700);
        alpha = from + (to - from) * k;
        glass.forEach(({ m, c }) => m.pbrMetallicRoughness.setBaseColorFactor([c[0], c[1], c[2], c[3] * alpha]));
        if (k < 1) fade = requestAnimationFrame(step);
      };
      fade = requestAnimationFrame(step);
    };

    const flyTo = (hot) => {
      if (!mv) return;
      mv.autoRotate = false;
      mv.cameraTarget = hot ? hot.dataset.target : 'auto auto auto';
      mv.cameraOrbit = hot?.dataset.orbit || home();
      xray(Boolean(hot && 'xray' in hot.dataset));
    };
    if (mv) {
      // Start as soon as the page has finished loading (so the car is ready before anyone scrolls to it),
      // or earlier if the visitor jumps straight to the cabin.
      let started = false;
      const start = () => {
        if (started) return;
        started = true;
        near.disconnect();
        const s = document.createElement('script');
        s.type = 'module';
        s.src = stage.dataset.mvSrc;
        document.head.append(s);
        mv.setAttribute('loading', 'eager');
        mv.setAttribute('src', mv.dataset.src);
      };
      const near = new IntersectionObserver(([e]) => { if (e.isIntersecting) start(); }, { rootMargin: '600px 0px' });
      near.observe(stage);
      const idle = () => (window.requestIdleCallback ? requestIdleCallback(start, { timeout: 2500 }) : setTimeout(start, 600));
      ['scroll', 'wheel', 'touchstart', 'pointerdown', 'keydown'].forEach((t) => addEventListener(t, idle, { once: true, passive: true }));
      mv.setAttribute('camera-orbit', home());
      mv.addEventListener('load', () => {
        mv.setAttribute('data-loaded', '');
        const names = (mv.dataset.xrayMaterials || '').split(',').map((s) => s.trim()).filter(Boolean);
        glass = mv.model.materials
          .filter((m) => names.includes(m.name))
          .map((m) => {
            m.setAlphaMode('BLEND');
            return { m, c: [...m.pbrMetallicRoughness.baseColorFactor] };
          });
      });
      if (reduce) mv.removeAttribute('auto-rotate');
    }

    const select = (id, fly) => {
      triggers.forEach((t) => t.setAttribute('aria-pressed', String(t.dataset.zone === id)));
      panels.forEach((p) => { p.hidden = p.dataset.zonePanel !== id; });
      const dot = dots.find((d) => d.dataset.zone === id);
      const hot = mv && hots.find((h) => h.dataset.zone === id);
      if (fly && mv) {
        flyTo(hot);
        root.classList.add('is-focused');
      }
      if (fly && dot && car) {
        car.style.setProperty('--zx', dot.style.getPropertyValue('--x'));
        car.style.setProperty('--zy', dot.style.getPropertyValue('--y'));
        root.classList.add('is-focused');
      }
      // Scroll only the chip strip; scrollIntoView would also shift the clipped section sideways on phones.
      const chip = root.querySelector('.kn-cabin__chip[aria-pressed="true"]');
      const strip = chip?.parentElement;
      if (strip && strip.scrollWidth > strip.clientWidth) {
        strip.scrollTo({ left: chip.offsetLeft - (strip.clientWidth - chip.offsetWidth) / 2, behavior: reduce ? 'auto' : 'smooth' });
      }
    };
    root.addEventListener('click', (e) => {
      const t = e.target.closest('[data-zone]');
      if (!t) return;
      if (t.getAttribute('aria-pressed') === 'true' && root.classList.contains('is-focused')) {
        root.classList.remove('is-focused');
        flyTo(null);
        return;
      }
      select(t.dataset.zone, true);
      // Phones: bring the product row into view (vertical only) once the camera has moved.
      const panel = panels.find((p) => !p.hidden);
      if (panel && matchMedia('(max-width: 989px)').matches) {
        const r = panel.getBoundingClientRect();
        const over = r.bottom - innerHeight + 16;
        if (over > 0) scrollBy({ top: Math.min(over, r.top - 72), behavior: reduce ? 'auto' : 'smooth' });
      }
    });

    /* Gentle tilt with the pointer (desktop) */
    const view = root.querySelector('.kn-cabin__view');
    if (view && car && !reduce && matchMedia('(hover: hover)').matches) {
      view.addEventListener('pointermove', (e) => {
        const r = view.getBoundingClientRect();
        car.style.setProperty('--tx', ((e.clientX - r.left) / r.width - 0.5) * 10 + 'deg');
        car.style.setProperty('--ty', ((e.clientY - r.top) / r.height - 0.5) * -8 + 'deg');
      });
      view.addEventListener('pointerleave', () => { car.style.setProperty('--tx', '0deg'); car.style.setProperty('--ty', '0deg'); });
    }
  });

  /* Story chapters */
  document.querySelectorAll('[data-kn-story]').forEach((root) => {
    const chapters = [...root.querySelectorAll('[data-chapter]')];
    const pics = [...root.querySelectorAll('[data-pic]')];
    const dots = [...root.querySelectorAll('.kn-story__dots span')];
    const show = (i) => {
      chapters.forEach((c, n) => c.classList.toggle('is-on', n === i));
      pics.forEach((p, n) => p.classList.toggle('is-on', n === i));
      dots.forEach((d, n) => d.classList.toggle('is-on', n === i));
    };
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) show(Number(e.target.dataset.chapter)); });
    }, {
      // Phones pin the picture over the top ~45% of the screen, so read the chapter in the middle of what's left.
      rootMargin: matchMedia('(max-width: 989px)').matches ? '-68% 0px -30% 0px' : '-45% 0px -45% 0px',
    });
    chapters.forEach((c) => io.observe(c));
  });

  /* Lineup rail */
  document.querySelectorAll('[data-kn-rail]').forEach((root) => {
    const rail = root.querySelector('[data-rail]');
    const prev = root.querySelector('[data-rail-prev]');
    const next = root.querySelector('[data-rail-next]');
    if (!rail) return;
    const step = () => (rail.querySelector('li')?.getBoundingClientRect().width || 300) + 20;
    const sync = () => {
      if (prev) prev.disabled = rail.scrollLeft < 8;
      if (next) next.disabled = rail.scrollLeft + rail.clientWidth > rail.scrollWidth - 8;
    };
    prev?.addEventListener('click', () => rail.scrollBy({ left: -step() * 2, behavior: 'smooth' }));
    next?.addEventListener('click', () => rail.scrollBy({ left: step() * 2, behavior: 'smooth' }));
    rail.addEventListener('scroll', sync, { passive: true });
    sync();
  });

  /* Glow that follows the cursor */
  document.querySelectorAll('[data-kn-glow]').forEach((el) => {
    el.addEventListener('pointermove', (e) => {
      const r = el.getBoundingClientRect();
      el.style.setProperty('--mx', e.clientX - r.left + 'px');
      el.style.setProperty('--my', e.clientY - r.top + 'px');
    });
  });

  /* Counting numbers */
  const counter = new IntersectionObserver((entries) => {
    entries.forEach((e) => {
      if (!e.isIntersecting) return;
      counter.unobserve(e.target);
      const el = e.target;
      const raw = el.dataset.count;
      const parts = raw.split(/(\d+)/);
      const nums = parts.map((s) => (/^\d+$/.test(s) ? Number(s) : null));
      if (reduce || !nums.some((n) => n !== null)) return;
      const t0 = performance.now();
      const dur = 1400;
      const tick = (t) => {
        const k = Math.min(1, (t - t0) / dur);
        const ease = 1 - Math.pow(1 - k, 4);
        el.textContent = parts.map((s, i) => (nums[i] === null ? s : Math.round(nums[i] * ease))).join('');
        if (k < 1) requestAnimationFrame(tick); else el.textContent = raw;
      };
      requestAnimationFrame(tick);
    });
  }, { threshold: 0.6 });
  document.querySelectorAll('[data-count]').forEach((el) => counter.observe(el));
})();
